/**
 * The task centre: what is running now, and what recently finished.
 *
 * **Not the notification bell, and deliberately next to it.** The two answer
 * different questions and the product needs both:
 *
 *   bell  - *events*. Something happened; here is a record you can mark read.
 *   tasks - *state*. Something is happening; here is where it has got to.
 *
 * A finished reconstruction produces one notification forever and one task
 * that ages out. Merging them would mean either an inbox that keeps changing
 * its own entries or a work list that never empties.
 *
 * ## No fake progress
 *
 * There is no percentage in this component and no progress bar, because the
 * worker publishes a status and, on failure, the stage it blamed - it does
 * not publish a fraction. A bar filled from elapsed time is the specific lie
 * this file exists to refuse: it moves smoothly while a stalled job sits at
 * the same stage, and it reaches 90% and stops for everything slow.
 *
 * ## One timer, and only while there is something to watch
 *
 * A single interval for the whole application - not one per task, not one per
 * card. It polls only while at least one job is queued or running, so an
 * account with nothing in flight makes one request on mount and then stops
 * until something starts. Requests never overlap: the interval is far longer
 * than a request takes and the last one is aborted on unmount.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { listTasks } from "@/api/client";
import type { Task } from "@/api/types";
import { IconGrid } from "@/components/icons";
import { paths } from "@/app/paths";
import { RECONSTRUCTION_TERMS } from "@/i18n/terms";
import { formatDateTime } from "@/lib/format";
import "./workflow.css";

/** Matches the notification bell: the events behind this take minutes. */
const POLL_MS = 15_000;
const TASK_LIMIT = 20;

const isActive = (task: Task): boolean =>
  task.status === "queued" || task.status === "running";

/**
 * What a task's state is called.
 *
 * A running job says the phase the worker reported, in the same words the
 * reconstruction screen uses. It used to say "Seyrek rekonstrüksiyon
 * çalışıyor" for every running job - written when sparse was the only stage a
 * deployment could reach, and left in place after the GPU worker started
 * meshing, so the header and the screen behind it described one run
 * differently.
 *
 * A running job whose worker has not reported yet says so; it does not borrow
 * the first phase as a guess.
 */
export function taskStageText(task: Task): string {
  switch (task.status) {
    case "queued":
      return "Sırada";
    case "running":
      return task.current_phase
        ? (RECONSTRUCTION_TERMS.phase[task.current_phase] ?? RECONSTRUCTION_TERMS.phaseUnknown)
        : RECONSTRUCTION_TERMS.phaseUnknown;
    case "succeeded":
      return "Tamamlandı";
    case "failed":
      return "Başarısız";
    case "cancelled":
      return "İptal edildi";
  }
}

export function TaskCenter() {
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [failed, setFailed] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  // Read by the interval effect without making it a dependency: putting the
  // task list in the dependency array would tear down and rebuild the timer
  // on every poll, which is how a "single timer" quietly becomes many.
  const activeRef = useRef(false);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      const next = await listTasks({ limit: TASK_LIMIT }, signal ? { signal } : {});
      setTasks(next);
      activeRef.current = next.some(isActive);
      setFailed(false);
    } catch {
      // A failed poll leaves the last known list on screen. The panel says
      // something when it is opened; the header must not start flashing.
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    const timer = window.setInterval(() => {
      // The whole polling budget: nothing in flight, nothing to ask about.
      // The list still refreshes when the panel is opened, so starting a job
      // from another tab is picked up the moment somebody looks.
      if (!activeRef.current) return;
      void refresh(controller.signal);
    }, POLL_MS);
    return () => {
      window.clearInterval(timer);
      controller.abort();
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const activeCount = (tasks ?? []).filter(isActive).length;

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (!next) return;
    try {
      setTasks(await listTasks({ limit: TASK_LIMIT }));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }

  return (
    <div className="tasks" ref={root}>
      <button
        type="button"
        className="tasks__button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          activeCount > 0 ? `İşlemler, ${activeCount} işlem sürüyor` : "İşlemler"
        }
        onClick={() => void toggle()}
      >
        <IconGrid size={18} />
        {/* The badge counts running work, and is hidden when there is none -
            a permanent zero is noise. `aria-hidden` because the count is
            already in the button's accessible name above. */}
        {activeCount > 0 && (
          <span className="tasks__count" aria-hidden="true">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="tasks__panel" role="dialog" aria-label="İşlemler">
          <h2 className="tasks__heading">İşlemler</h2>
          {failed ? (
            <p className="tasks__empty">İşlem durumu şu anda alınamıyor.</p>
          ) : tasks === null ? (
            <p className="tasks__empty">Yükleniyor…</p>
          ) : tasks.length === 0 ? (
            <p className="tasks__empty">Devam eden işlem yok.</p>
          ) : (
            <ul className="tasks__list">
              {tasks.map((task) => (
                <li key={task.job_id}>
                  <Link
                    className="tasks__item"
                    to={paths.projectReconstruction(task.project_id)}
                    onClick={() => setOpen(false)}
                  >
                    <span className="tasks__project">
                      {isActive(task) && <span className="tasks__pulse" aria-hidden="true" />}
                      {task.project_name}
                    </span>
                    <span className="tasks__stage">
                      {taskStageText(task)}
                      {" · "}
                      {formatDateTime(task.finished_at ?? task.started_at ?? task.created_at)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
